import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Clock, Ticket, Star, CalendarDays } from "lucide-react";
import { motion } from "framer-motion";
import { api, apiError, imgUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const PALETTE = ["#E7E3F2", "#F7E3D7", "#E4EDDF", "#F5EED8"];
const inputCls = "w-full bg-white/80 border border-[#1A1A1A]/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]/40 transition-colors placeholder:text-[#9A9A94]";

export default function Learn() {
  const [offerings, setOfferings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [openFor, setOpenFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [sp] = useSearchParams();
  const toastOnce = useRef(false);

  const load = () => {
    api.get("/learn/offerings").then((r) => setOfferings(r.data)).catch(() => {});
    api.get("/reviews?category=learn").then((r) => setReviews(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const v = sp.get("booked");
    if (toastOnce.current || !v) return;
    toastOnce.current = true;
    setTimeout(() => {
      if (v === "success") {
        toast.success("Booked! We'll contact you shortly to arrange your session date and time.");
        load();
      } else {
        toast.info("Checkout cancelled — no charge was made.");
      }
      window.history.replaceState({}, "", window.location.pathname);
    }, 50);
  }, [sp]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const book = async (o) => {
    setLoading(true);
    try {
      const r = await api.post("/learn/checkout", { offering_id: o.id, ...form, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setLoading(false);
    }
  };

  return (
    <main data-testid="learn-page" className="pt-32 pb-28 overflow-hidden">
      <Seo title="Learn With Bahare — Photography Classes & Mentoring in Los Angeles"
        description="In-person, hands-on photography training in Los Angeles — private lessons, classes, and mentoring. Book and pay online, then we schedule together." />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="text-center">
          <motion.span initial={{ rotate: -6, scale: 0.8, opacity: 0 }} animate={{ rotate: -4, scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-block bg-[#E4572E] text-[#F9F9F8] text-[10px] uppercase tracking-[0.25em] px-5 py-2 rounded-full mb-8">
            Education · Los Angeles
          </motion.span>
          <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[1.02]">
            Learn With <span className="italic text-[#E4572E]">Bahare.</span>
          </h1>
          <p className="mt-6 text-sm text-[#4A4A4A] max-w-md mx-auto leading-relaxed">
            In-person, hands-on training in Los Angeles — for anyone who wants to see differently.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-20">
          {offerings.map((o, i) => (
            <Reveal key={o.id} delay={(i % 3) * 0.08}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`learn-offering-${o.id}`}
                className="rounded-[2rem] overflow-hidden h-full flex flex-col"
                style={{ backgroundColor: PALETTE[i % PALETTE.length] }}>
                {o.image && (
                  <div className="overflow-hidden">
                    <img src={imgUrl(o.image)} alt={o.title} loading="lazy"
                      className="w-full aspect-[16/10] object-cover hover:scale-[1.03] transition-transform duration-[1200ms] ease-out" />
                  </div>
                )}
                <div className="p-8 flex flex-col flex-1">
                  <div className="flex flex-wrap gap-2 mb-5">
                    {o.date && (
                      <span className="inline-flex items-center gap-1.5 bg-white/70 text-[11px] uppercase tracking-[0.12em] px-4 py-2 rounded-full" data-testid={`learn-date-${o.id}`}>
                        <CalendarDays size={12} strokeWidth={1.5} /> {o.date}
                      </span>
                    )}
                    {o.duration && (
                      <span className="inline-flex items-center gap-1.5 bg-white/70 text-[11px] uppercase tracking-[0.12em] px-4 py-2 rounded-full">
                        <Clock size={12} strokeWidth={1.5} /> {o.duration}
                      </span>
                    )}
                    {o.price > 0 && (
                      <span className="inline-flex items-center gap-1.5 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.12em] px-4 py-2 rounded-full" data-testid={`learn-price-${o.id}`}>
                        <Ticket size={12} strokeWidth={1.5} /> ${o.price}
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl font-light mb-3">{o.title}</h3>
                  {o.description && <p className="text-sm text-[#3d3d3a] leading-relaxed mb-8 whitespace-pre-wrap break-words">{o.description}</p>}

                  <div className="mt-auto">
                    {!o.available ? (
                      <p className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A]">Currently unavailable</p>
                    ) : openFor === o.id ? (
                      <div className="bg-white/60 rounded-2xl p-5 space-y-3" data-testid={`learn-form-${o.id}`}>
                        <p className="text-xs text-[#7A7A7A] leading-relaxed bg-[#F5EED8] rounded-lg px-3 py-2">
                          Book and pay now. We'll contact you to arrange your session date and time.
                        </p>
                        <input placeholder="Name *" value={form.name} onChange={set("name")} data-testid={`learn-name-${o.id}`} className={inputCls} />
                        <input placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid={`learn-email-${o.id}`} className={inputCls} />
                        <input placeholder="Phone (optional)" type="tel" value={form.phone} onChange={set("phone")} data-testid={`learn-phone-${o.id}`} className={inputCls} />
                        <div className="flex gap-3 pt-1">
                          <button onClick={() => book(o)} disabled={loading || !form.name || !form.email}
                            data-testid={`learn-pay-${o.id}`}
                            className="flex-1 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] py-3.5 rounded-full hover:bg-[#E4572E] transition-colors disabled:opacity-50">
                            {loading ? "Sending…" : `Pay $${o.price} & Book`}
                          </button>
                          <button onClick={() => setOpenFor(null)}
                            className="text-[11px] uppercase tracking-[0.2em] text-[#7A7A7A] hover:text-[#1A1A1A] px-3">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setOpenFor(o.id)} data-testid={`learn-book-${o.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] py-4 rounded-full hover:bg-[#E4572E] transition-colors">
                        Book Now — ${o.price} <ArrowRight size={13} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
        {offerings.length === 0 && (
          <p className="text-sm text-[#7A7A7A] text-center mt-20" data-testid="learn-empty">New classes are being planned. Check back soon.</p>
        )}

        {reviews.length > 0 && (
          <div className="max-w-2xl mt-24 mx-auto" data-testid="learn-testimonials">
            <Reveal className="text-center mb-10">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7A7A]">Kind words from students</p>
            </Reveal>
            <div className="space-y-6">
              {reviews.map((r) => (
                <Reveal key={r.id}>
                  <div className="bg-white/70 rounded-2xl p-6 md:p-8" data-testid={`learn-review-${r.id}`}>
                    <div className="flex items-center gap-1.5 mb-3 text-[#C9A24B]">
                      {Array.from({ length: r.stars || 5 }).map((_, i) => <Star key={i} size={14} fill="currentColor" strokeWidth={0} />)}
                    </div>
                    <p className="font-serif text-lg italic leading-relaxed text-[#3d3d3a]">“{r.text}”</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#7A7A7A] mt-4">{r.name}{r.session_type ? ` · ${r.session_type}` : ""}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
